import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

import { PlayerProfile } from '@wwe-portal/entities/player.type';
import { percentFormatter, tryFormatThousands } from '@wwe-portal/utils/format';
import { NOT_IN_FACTIONS } from '@wwe-portal/web/utils/config';

export function usePlayerDetails(
  t: ReturnType<typeof useTranslations>,
  profile?: PlayerProfile
) {
  const daysSinceInstall = useMemo(() => {
    if (profile && profile.days_since_install) {
      return `${tryFormatThousands(profile.days_since_install || 0)} ${t('days')}`;
    } else {
      return '';
    }
  }, [profile]);

  const rosterSize = useMemo(() => {
    if (profile) {
      return (profile.female_ss_count ?? 0) + (profile.male_ss_count ?? 0);
    } else {
      return 'N/A';
    }
  }, [profile]);
  const totalRosterTalent = useMemo(() => {
    if (profile) {
      return tryFormatThousands(+(profile.total_roster_talent || ''));
    } else {
      return '0';
    }
  }, [profile]);

  const leagueLevel = useMemo(() => {
    return Number(profile?.league_level || 1);
  }, [profile]);

  const mostConsecutiveLoginDays = useMemo(() => {
    if (profile && profile.most_consecutive_login_days) {
      return `${tryFormatThousands(profile.most_consecutive_login_days)} ${t('days')}`;
    } else {
      return '';
    }
  }, [profile]);

  const factionName = useMemo(() => {
    if (profile) {
      if (profile.faction_name && profile.faction_name !== NOT_IN_FACTIONS) {
        return profile.faction_name;
      } else if (
        profile.alliance_name &&
        profile.alliance_name !== NOT_IN_FACTIONS
      ) {
        return profile.alliance_name;
      } else {
        return '';
      }
    } else {
      return '';
    }
  }, [profile]);
  const factionTotalRosterTalent = useMemo(() => {
    return profile?.faction_total_roster_talent || 0;
  }, [profile]);
  const avatarRarity = useMemo(() => {
    return profile?.avatarRarity || '';
  }, [profile]);

  const allTimeWins = useMemo(() => {
    return tryFormatThousands(profile?.all_time_wins ?? 0);
  }, [profile]);
  const last30dWins = useMemo(() => {
    return tryFormatThousands(profile?.last_30d_wins ?? 0);
  }, [profile]);

  const allTimeBattles = useMemo(() => {
    return tryFormatThousands(profile?.all_time_battles ?? 0);
  }, [profile]);
  const last30dBattles = useMemo(() => {
    return tryFormatThousands(profile?.last_30d_battles ?? 0);
  }, [profile]);

  const allTimeWinRate = useMemo(() => {
    return percentFormatter(profile?.all_time_win_rate ?? 0, 1);
  }, [profile]);
  const last30dWinRate = useMemo(() => {
    return percentFormatter(profile?.last_30d_win_rate ?? 0, 1);
  }, [profile]);

  return {
    daysSinceInstall,
    rosterSize,
    totalRosterTalent,
    leagueLevel,
    mostConsecutiveLoginDays,
    factionName,
    factionTotalRosterTalent,
    avatarRarity,
    allTimeWins,
    last30dWins,
    allTimeBattles,
    last30dBattles,
    allTimeWinRate,
    last30dWinRate,
  };
}
